import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useStore } from "@/lib/store";
import { apiRequest } from "@/lib/queryClient";
import type {
  ParsedStoriesScript,
  ParsedStoryFrame,
  StoriesKeywordHighlight,
} from "./stories-parser";
import EditFrameModal from "./edit-frame-modal";
import RenderStoriesButton, { type RenderedFrame } from "./render-stories-button";

interface Props {
  script: ParsedStoriesScript;
  /** When provided, the preview also renders the "Generate" button below the
   *  frames and feeds it the (possibly edited) script as `editedScript`. */
  conversationId?: string;
  messageId?: string;
}

function escRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Split text into [plain, highlighted, plain, highlighted, ...] runs based on
 *  keyword matches. Match is case-insensitive, longest-first to avoid partial
 *  shadowing (e.g. "AI" inside "TRAINING"). */
function applyHighlights(text: string, kws: StoriesKeywordHighlight[]): React.ReactNode[] {
  if (!text) return [];
  if (!kws || kws.length === 0) return [text];
  const sorted = [...kws].sort((a, b) => b.keyword.length - a.keyword.length);
  const re = new RegExp(`(${sorted.map((k) => escRe(k.keyword)).join("|")})`, "gi");
  const parts = text.split(re);
  const styleByLower = new Map<string, StoriesKeywordHighlight["style"]>();
  sorted.forEach((k) => styleByLower.set(k.keyword.toLowerCase(), k.style));

  return parts.map((part, i) => {
    const style = styleByLower.get(part.toLowerCase());
    if (!style) return <span key={i}>{part}</span>;
    if (style === "red-text") {
      return (
        <span key={i} className="text-[#ef3e29] font-extrabold">
          {part}
        </span>
      );
    }
    if (style === "underline") {
      return (
        <span
          key={i}
          className="underline decoration-[#ef3e29] decoration-[3px] underline-offset-2 font-extrabold"
        >
          {part}
        </span>
      );
    }
    if (style === "highlight-box") {
      return (
        <span key={i} className="bg-[#ef3e29] text-white px-1 rounded font-extrabold">
          {part}
        </span>
      );
    }
    if (style === "circle") {
      return (
        <span
          key={i}
          className="font-extrabold inline-block relative"
          style={{ padding: "1px 4px" }}
        >
          {part}
          {/* SVG ellipse overlaid — mirrors renderer's handDrawnCircle (two offset ellipses). */}
          <svg
            className="absolute inset-0 w-full h-full overflow-visible pointer-events-none"
            style={{ transform: "rotate(-4deg)" }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <ellipse
              cx="50%"
              cy="44%"
              rx="54%"
              ry="68%"
              fill="none"
              stroke="#ef3e29"
              strokeWidth="1.5"
              strokeOpacity="0.95"
              strokeLinecap="round"
            />
            <ellipse
              cx="50%"
              cy="44%"
              rx="56%"
              ry="66%"
              fill="none"
              stroke="#ef3e29"
              strokeWidth="1.5"
              strokeOpacity="0.55"
              strokeLinecap="round"
            />
          </svg>
        </span>
      );
    }
    if (style === "arrow-underline") {
      return (
        <span
          key={i}
          className="font-extrabold inline-block"
          style={{
            backgroundImage:
              'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 60 8%22 preserveAspectRatio=%22none%22><path d=%22M0 4 C 7.5 0,15 8,22.5 4 S 37.5 0,45 4 S 60 8,60 4%22 stroke=%22%23ef3e29%22 stroke-width=%222%22 fill=%22none%22 stroke-linecap=%22round%22/></svg>")',
            backgroundRepeat: "repeat-x",
            backgroundPosition: "bottom left",
            backgroundSize: "60px 5px",
            paddingBottom: "5px",
          }}
        >
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function FramePreviewCard({
  frame,
  total,
  onEdit,
  backgroundUrl,
  t,
}: {
  frame: ParsedStoryFrame;
  total: number;
  onEdit: () => void;
  /** VP-230 4b: realne wygenerowane tło klatki; bez niego karta pokazuje
   *  placeholder-gradient z informacją, że tło powstanie przy generacji. */
  backgroundUrl?: string;
  t: (k: string, fallback?: string) => string;
}): JSX.Element {
  // Body may contain multiple paragraphs separated by \n\n (or single \n).
  const bodyParagraphs = (frame.body ?? "")
    .split(/\n{2,}|\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div
      className="flex-shrink-0 w-[180px] aspect-[9/16] rounded-2xl bg-gradient-to-br from-zinc-800 via-zinc-900 to-black border border-white/[0.08] relative overflow-hidden shadow-lg group"
      data-testid={`story-frame-preview-${frame.index}`}
    >
      {backgroundUrl && (
        <img
          src={backgroundUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={`story-frame-preview-bg-${frame.index}`}
          onError={(e) => {
            // Wygasły signed URL → wróć do gradientu zamiast ikony zepsutego obrazka
            e.currentTarget.style.display = "none";
          }}
        />
      )}
      {/* Edit button (appears on hover, top-right) */}
      <button
        onClick={onEdit}
        className="absolute top-2 right-2 z-20 bg-black/60 hover:bg-[#ef3e29] text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
        title={t("storiesCreator.preview.edit", "Edytuj klatkę")}
        data-testid={`btn-edit-frame-${frame.index}`}
      >
        <Pencil size={11} />
      </button>

      {/* Progress bars */}
      <div className="absolute top-2 left-2 right-8 flex gap-1 z-10">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-[2px] flex-1 rounded-full ${i < frame.index ? "bg-white" : "bg-white/30"}`}
          />
        ))}
      </div>

      {/* Role badge */}
      <div className="absolute top-6 left-2 z-10">
        <span className="text-[8px] font-bold uppercase tracking-wider bg-black/60 text-white px-1.5 py-0.5 rounded">
          {frame.index}/{total} ·{" "}
          {t(`storiesCreator.preview.roles.${frame.role}`, frame.role.toUpperCase())}
        </span>
      </div>

      {/* Headline + body — layout mirrors text-overlay-renderer.ts positioning */}
      {/* Headline block */}
      <div
        className={`absolute inset-x-3 z-10 ${
          frame.overlayLayout === "top"
            ? "top-[14%]"
            : frame.overlayLayout === "bottom"
              ? "bottom-[20%]"
              : frame.overlayLayout === "split"
                ? "top-[14%]"
                : "top-1/2 -translate-y-1/2"
        }`}
      >
        <div className="text-white font-black text-[15px] leading-[1.1] drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
          {applyHighlights(frame.headline, frame.headlineKeywords ?? [])}
        </div>
        {/* Body: for split layout body is pinned to bottom, otherwise flows after headline */}
        {bodyParagraphs.length > 0 && frame.overlayLayout !== "split" && (
          <div className="text-white/95 text-[10px] mt-2 leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] space-y-1.5">
            {bodyParagraphs.map((p, i) => (
              <p key={i}>{applyHighlights(p, frame.bodyKeywords ?? [])}</p>
            ))}
          </div>
        )}
      </div>
      {/* Split layout: body pinned to bottom (mirrors renderer's bodyStartBaselineY = h - SAFE_BOTTOM) */}
      {bodyParagraphs.length > 0 && frame.overlayLayout === "split" && (
        <div className="absolute inset-x-3 bottom-[20%] z-10 text-white/95 text-[10px] leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] space-y-1.5">
          {bodyParagraphs.map((p, i) => (
            <p key={i}>{applyHighlights(p, frame.bodyKeywords ?? [])}</p>
          ))}
        </div>
      )}

      {/* CTA */}
      {frame.cta?.text && (
        <div className="absolute bottom-3 left-3 right-3 z-10">
          <div className="bg-[#ef3e29] text-white text-center text-[10px] font-bold py-2 rounded-md uppercase tracking-wide">
            {frame.cta.text}
          </div>
        </div>
      )}

      {/* Visual prompt hint / info o tle (VP-230 4b: jasna komunikacja placeholdera) */}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/60 text-white/40 text-[7px] truncate z-0">
        {backgroundUrl
          ? frame.visualPrompt
          : `${t("storiesCreator.preview.backgroundPending", "Tło zostanie wygenerowane")} · ${frame.visualPrompt}`}
      </div>
    </div>
  );
}

/** Strip frontend-only fields before sending edited script back to backend.
 *  The server schema (StoryFrameSchema) doesn't accept `keywordHighlights` or
 *  the `'middle'` overlay alias used by the legacy preview. */
function toServerScript(script: ParsedStoriesScript): unknown {
  return {
    sharedBackgroundPrompt: script.sharedBackgroundPrompt,
    frames: script.frames.map((f) => ({
      index: f.index,
      role: f.role,
      headline: f.headline,
      headlineKeywords: (f.headlineKeywords ?? []).map((k) => ({
        text: k.keyword,
        style: k.style,
      })),
      body: f.body ?? "",
      bodyKeywords: (f.bodyKeywords ?? []).map((k) => ({ text: k.keyword, style: k.style })),
      visualPrompt: f.visualPrompt,
      overlayLayout: f.overlayLayout === "middle" ? "center" : (f.overlayLayout ?? "center"),
      textColor: f.textColor ?? "white",
      cta: f.cta,
    })),
  };
}

type SequenceForMessageResponse = {
  sequenceId: string;
  frames: Array<{ index: number; backgroundUrl?: string }>;
} | null;

/**
 * VP-230 4b: w trybie as-is wgrane zdjęcia SĄ finalnymi tłami, więc podgląd
 * pokazuje je od razu. Mapowanie lustrzane wobec BackgroundGenerator:
 * przypięte klatki (r2Key → frameIndex) mają priorytet, reszta round-robin
 * po niepodpiętej części puli.
 */
function mapUserBackgroundsToFrames(
  frames: ParsedStoryFrame[],
  r2Keys: string[],
  assignments: Record<string, number>,
  previews: Record<string, string>
): Record<number, string> {
  if (r2Keys.length === 0) return {};
  const pinnedKeys = new Set(Object.keys(assignments));
  const autoPool = r2Keys.filter((k) => !pinnedKeys.has(k));
  const fallbackPool = autoPool.length > 0 ? autoPool : r2Keys;
  const keyByFrameIndex = new Map<number, string>();
  for (const [r2Key, frameIndex] of Object.entries(assignments)) {
    keyByFrameIndex.set(frameIndex, r2Key); // ostatni wygrywa — jak w routes
  }

  const byIndex: Record<number, string> = {};
  let cursor = 0;
  for (const frame of frames) {
    const pinned = keyByFrameIndex.get(frame.index);
    const key = pinned ?? fallbackPool[cursor % fallbackPool.length];
    if (!pinned) cursor += 1;
    const url = previews[key];
    if (url) byIndex[frame.index] = url;
  }
  return byIndex;
}

export default function StoryFramesPreview({
  script: initialScript,
  conversationId,
  messageId,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [script, setScript] = useState<ParsedStoriesScript>(initialScript);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  // VP-230 4b: tła ze świeżo ukończonego renderu (bez czekania na refetch)
  const [liveBackgrounds, setLiveBackgrounds] = useState<Record<number, string>>({});
  const editingFrame =
    editingIndex !== null ? (script.frames.find((f) => f.index === editingIndex) ?? null) : null;

  // VP-230 4b: rehydratacja teł po reloadzie — ostatnia sekwencja dla tej wiadomości.
  // 404 (nic jeszcze nie wygenerowano) jest normalne — traktujemy jak brak danych.
  const { data: persistedSequence } = useQuery<SequenceForMessageResponse>({
    queryKey: ["stories-sequence-for-message", conversationId, messageId],
    enabled: Boolean(conversationId && messageId),
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      try {
        return (await apiRequest(
          "GET",
          `/api/stories-creator/conversations/${conversationId}/messages/${messageId}/sequence`
        )) as SequenceForMessageResponse;
      } catch {
        return null;
      }
    },
  });

  const {
    storiesUserBackgroundR2Keys,
    storiesUserBackgroundAssignments,
    storiesUserBackgroundMode,
    storiesUserBackgroundPreviews,
  } = useStore();

  const backgroundByIndex: Record<number, string> = {};
  persistedSequence?.frames?.forEach((f) => {
    if (f.backgroundUrl) backgroundByIndex[f.index] = f.backgroundUrl;
  });
  Object.assign(backgroundByIndex, liveBackgrounds);
  // Wgrane tła (as-is) nadpisują starsze rendery — to one pójdą do NASTĘPNEJ generacji
  if (storiesUserBackgroundR2Keys.length > 0 && storiesUserBackgroundMode === "as-is") {
    Object.assign(
      backgroundByIndex,
      mapUserBackgroundsToFrames(
        script.frames,
        storiesUserBackgroundR2Keys,
        storiesUserBackgroundAssignments,
        storiesUserBackgroundPreviews
      )
    );
  }

  const handleFramesRendered = (frames: RenderedFrame[]): void => {
    const next: Record<number, string> = {};
    frames.forEach((f) => {
      if (f.backgroundUrl) next[f.index] = f.backgroundUrl;
    });
    if (Object.keys(next).length > 0) setLiveBackgrounds(next);
  };

  const handleSave = (next: ParsedStoryFrame): void => {
    setScript({
      ...script,
      frames: script.frames.map((f) => (f.index === next.index ? next : f)),
    });
    setIsDirty(true);
    setEditingIndex(null);
  };

  return (
    <div className="space-y-3">
      {script.visualStyleNote && (
        <div className="text-[11px] text-white/40 italic px-1">
          {t("storiesCreator.preview.visualNote", "Styl wizualny")}: {script.visualStyleNote}
        </div>
      )}
      {isDirty && (
        <div className="text-[11px] text-amber-400/80 italic px-1">
          ✏️ Edytowano — twoje zmiany zostaną wysłane przy następnej generacji.
        </div>
      )}
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2">
        {script.frames.map((frame) => (
          <FramePreviewCard
            key={frame.index}
            frame={frame}
            total={script.frames.length}
            onEdit={() => setEditingIndex(frame.index)}
            backgroundUrl={backgroundByIndex[frame.index]}
            t={t as (k: string, fallback?: string) => string}
          />
        ))}
      </div>

      {conversationId && messageId && (
        <RenderStoriesButton
          conversationId={conversationId}
          messageId={messageId}
          editedScript={isDirty ? toServerScript(script) : undefined}
          onFramesRendered={handleFramesRendered}
        />
      )}

      {editingFrame && (
        <EditFrameModal
          frame={editingFrame}
          onClose={() => setEditingIndex(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
